// Build-a-bundle: collects chosen variants, shows live tier savings, and adds every piece to the cart at once.
class SBundleBuilder extends HTMLElement {
  #ac;
  #items = [];
  #tiers = [];
  #busy = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#tiers = (this.dataset.tiers || '')
      .split(',')
      .map((pair) => pair.split(':').map((value) => Number.parseFloat(value)))
      .filter(([count, percent]) => count > 0 && percent >= 0)
      .map(([count, percent]) => ({ count, percent }))
      .sort((a, b) => a.count - b.count);

    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.#render();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #onChange(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-variant-select]')) return;
    const card = select.closest('[data-bundle-product]');
    const button = card?.querySelector('[data-add-piece]');
    const option = select.selectedOptions[0];
    if (!button || !option) return;
    button.dataset.price = option.dataset.price || button.dataset.price;
    button.dataset.variantTitle = option.dataset.variantTitle || '';
    if (option.dataset.image) button.dataset.image = option.dataset.image;
  }

  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const addButton = target.closest('[data-add-piece]');
    if (addButton) {
      this.#addPiece(addButton);
      return;
    }

    const removeButton = target.closest('[data-remove]');
    if (removeButton) {
      this.#items.splice(Number(removeButton.dataset.remove), 1);
      this.#render();
      return;
    }

    if (target.closest('[data-submit]')) this.#submit();
  }

  #addPiece(button) {
    const card = button.closest('[data-bundle-product]');
    const input = card?.querySelector('[data-variant-select]');
    const variantId = input?.value;
    if (!variantId) return;

    this.#items.push({
      variantId,
      title: button.dataset.productTitle || '',
      variantTitle: button.dataset.variantTitle || '',
      price: Number.parseInt(button.dataset.price || '0', 10),
      image: button.dataset.image || '',
    });

    const label = button.textContent;
    button.textContent = this.dataset.labelAdded || label;
    setTimeout(() => {
      button.textContent = this.dataset.labelAdd || label;
    }, 1200);

    this.#render();
  }

  #activeTier() {
    const count = this.#items.length;
    return this.#tiers.filter((tier) => count >= tier.count).pop() || null;
  }

  #render() {
    const count = this.#items.length;
    const tier = this.#activeTier();
    const percent = tier ? tier.percent : 0;
    const subtotal = this.#items.reduce((sum, item) => sum + item.price, 0);
    const savings = Math.round((subtotal * percent) / 100);
    const maxTier = this.#tiers[this.#tiers.length - 1];
    const nextTier = this.#tiers.find((t) => t.count > count);

    this.querySelectorAll('[data-count]').forEach((el) => (el.textContent = String(count)));
    this.#setText('[data-subtotal]', this.#money(subtotal));
    this.#setText('[data-savings]', savings > 0 ? `−${this.#money(savings)}` : this.#money(0));
    this.#setText('[data-total]', this.#money(subtotal - savings));

    const progress = this.querySelector('[data-progress]');
    if (progress && maxTier) progress.style.width = `${Math.min(100, (count / maxTier.count) * 100)}%`;

    this.querySelectorAll('[data-tier]').forEach((el) => {
      el.toggleAttribute('data-active', tier !== null && Number(el.dataset.tier) === tier.count);
    });

    let status = this.dataset.labelEmpty || '';
    if (count > 0 && nextTier) {
      status = (this.dataset.labelNext || '')
        .replace('[count]', String(nextTier.count - count))
        .replace('[percent]', String(nextTier.percent));
    } else if (count > 0 && tier) {
      status = (this.dataset.labelMax || '').replace('[percent]', String(tier.percent));
    }
    this.#setText('[data-status]', status);

    const list = this.querySelector('[data-items]');
    if (list) {
      list.replaceChildren(...this.#items.map((item, index) => this.#itemNode(item, index)));
    }

    const submit = this.querySelector('[data-submit]');
    if (submit instanceof HTMLButtonElement) submit.disabled = this.#busy || count < (this.#tiers[0]?.count || 1);
  }

  #itemNode(item, index) {
    const template = this.querySelector('template[data-item-template]');
    const node = template?.content.firstElementChild?.cloneNode(true);
    if (!(node instanceof HTMLElement)) return document.createElement('li');

    const img = node.querySelector('[data-item-image]');
    if (img instanceof HTMLImageElement) {
      if (item.image) img.src = item.image;
      else img.remove();
    }
    const title = node.querySelector('[data-item-title]');
    if (title) title.textContent = item.title;
    const meta = node.querySelector('[data-item-meta]');
    if (meta) meta.textContent = [item.variantTitle, this.#money(item.price)].filter(Boolean).join(' · ');
    const remove = node.querySelector('[data-remove]');
    if (remove) {
      remove.dataset.remove = String(index);
      remove.setAttribute('aria-label', (this.dataset.labelRemove || '').replace('[title]', item.title));
    }
    return node;
  }

  async #submit() {
    if (this.#busy || this.#items.length === 0) return;
    this.#busy = true;
    const submit = this.querySelector('[data-submit]');
    const original = submit?.textContent || '';
    if (submit) submit.textContent = this.dataset.labelAdding || original;
    this.#showError('');
    this.#render();

    // One line per variant, quantities merged, tagged so the cart shows which pieces came from a bundle.
    const quantities = new Map();
    this.#items.forEach((item) => quantities.set(item.variantId, (quantities.get(item.variantId) || 0) + 1));
    const items = [...quantities].map(([id, quantity]) => ({ id: Number(id), quantity, properties: { _bundle: 'The Closet' } }));

    try {
      const response = await fetch(this.dataset.cartAddUrl || '', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
        signal: this.#ac?.signal,
      });
      if (!response.ok) throw new Error(String(response.status));
      this.#items = [];
      window.location.assign(this.dataset.cartUrl || '');
    } catch (error) {
      if (this.#ac?.signal.aborted) return;
      this.#showError(this.dataset.labelError || '');
      this.#busy = false;
      if (submit) submit.textContent = original;
      this.#render();
    }
  }

  #setText(selector, value) {
    this.querySelectorAll(selector).forEach((el) => (el.textContent = value));
  }

  #showError(message) {
    const el = this.querySelector('[data-error]');
    if (!el) return;
    el.textContent = message;
    el.hidden = !message;
  }

  #money(cents) {
    const format = this.dataset.moneyFormat || '${{amount}}';
    const value = (cents || 0) / 100;
    const withDelimiters = (number, decimals, thousands, decimal) => {
      const [whole, fraction] = number.toFixed(decimals).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        default:
          return withDelimiters(value, 2, ',', '.');
      }
    });
  }
}

if (!customElements.get('s-bundle-builder')) customElements.define('s-bundle-builder', SBundleBuilder);
